import React, { useEffect } from 'react';
import { Divider } from '@mui/material';
import { useLanguage } from '../../contexts/LanguageContext';
import { useCurrencyRate } from '../../hooks/useCurrencyRate';

interface PackageCardProps {
  pkg: {
    id: string;
    name: string;
    price: number;
    currency: string;
    description?: string;
    color: string;
    is_popular?: boolean;
    target_audience?: string;
    max_businesses?: number;
    is_active?: boolean;
  };
  billingPeriod: 'month' | 'year';
  displayCurrency: 'USD' | 'UAH';
  onBillingPeriodChange: (period: 'month' | 'year') => void;
  onDisplayCurrencyChange: (currency: 'USD' | 'UAH') => void;
  onSelect: (packageId: string) => void;
  selectButtonText?: string;
  mode?: 'client' | 'admin';
}

// Helper function to convert hex color to RGB
function hexToRgb(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : '0, 0, 0';
}

const PackageCard: React.FC<PackageCardProps> = ({
  pkg,
  billingPeriod,
  displayCurrency,
  onBillingPeriodChange,
  onDisplayCurrencyChange,
  onSelect,
  selectButtonText,
  mode = 'client'
}) => {
  const { language } = useLanguage();
  const { convertUSDToUAH, convertUAHToUSD, refetchRate } = useCurrencyRate();

  // Refetch rate when currency changes to ensure latest rate
  useEffect(() => {
    if (displayCurrency === 'UAH') {
      refetchRate();
    }
  }, [displayCurrency]); // Remove refetchRate from dependencies

  // Get display price based on selected currency and billing period
  const getDisplayPrice = () => {
    let price = pkg.price;
    let currency = displayCurrency;

    // Convert currency if needed
    if (displayCurrency === 'UAH' && pkg.currency === 'USD') {
      // Use dynamic USD rate from API
      price = convertUSDToUAH(price);
    } else if (displayCurrency === 'USD' && pkg.currency === 'UAH') {
      // Convert UAH to USD using dynamic rate
      price = convertUAHToUSD(price);
    }

    // Adjust for billing period
    if (billingPeriod === 'year') {
      price = price * 11;
    }

    return { price, currency };
  };

  const formatPrice = (price: number, currency: string) => {
    return new Intl.NumberFormat(language === 'ua' ? 'uk-UA' : 'en-US', {
      style: 'currency',
      currency: currency,
    }).format(price);
  };

  const { price, currency } = getDisplayPrice();

  // Determine border color and shadow based on mode and active status
  const borderColor = mode === 'admin' && pkg.is_active === false ? '#e0e0e0' : pkg.color;
  const boxShadow = pkg.is_popular
    ? `0 8px 24px rgba(${hexToRgb(pkg.color)}, 0.3)`
    : (mode === 'admin' && pkg.is_active ? `0 8px 24px rgba(${hexToRgb(pkg.color)}, 0.3)` : 'none');

  return (
    <div
      className={`master-plan-card ${mode === 'admin' && pkg.is_active === false ? 'inactive' : ''}`}
      style={{
        borderColor,
        boxShadow,
      }}
    >
      {/* Status badges for admin mode */}
      {mode === 'admin' && pkg.is_active !== undefined && (
        <>
          {pkg.is_active ? (
            <div className="master-popular-badge" style={{ backgroundColor: pkg.color, right: (pkg as any).is_popular ? '-12px' : '-12px' }}>
              {language === 'ua' ? 'Активний' : 'Active'}
            </div>
          ) : (
            <div className="master-popular-badge" style={{ backgroundColor: '#f44336', right: (pkg as any).is_popular ? '-12px' : '-12px' }}>
              {language === 'ua' ? 'Неактивний' : 'Inactive'}
            </div>
          )}
        </>
      )}

      {/* Popular badge */}
      {pkg.is_popular && (
        <div
          className="master-popular-badge"
          style={{
            backgroundColor: '#ff9800',
            left: (mode === 'admin' && pkg.is_active) ? '-12px' : 'auto',
            right: (mode === 'admin' && pkg.is_active) ? 'auto' : '-12px',
            marginTop: (mode === 'admin' && pkg.is_active) ? '5px' : '5px'
          }}
        >
          {language === 'ua' ? 'Популярний' : 'Popular'}
        </div>
      )}

      <div className="master-plan-header">
        <h3>{pkg.name}</h3>
        <div className="master-plan-price">
          <span className="master-price-amount">
            {formatPrice(price, currency)}
          </span>
        </div>

        {/* Enhanced Billing Period and Currency Toggles */}
        <div className="billing-period-toggle" style={{
          marginBottom: '8px',
          display: 'flex',
          gap: '6px',
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
          {/* Period Toggle */}
          <div style={{
            display: 'flex',
            background: '#f5f5f5',
            border: '1px solid #e0e0e0',
            borderRadius: '6px',
            padding: '2px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            transition: 'all 0.2s ease'
          }}>
            <button
              className={`period-btn ${billingPeriod === 'month' ? 'active' : ''}`}
              onClick={() => onBillingPeriodChange('month')}
              style={{
                padding: '4px 10px',
                border: 'none',
                background: billingPeriod === 'month' ? pkg.color : 'transparent',
                color: billingPeriod === 'month' ? '#fff' : '#666',
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: billingPeriod === 'month' ? '600' : '500',
                cursor: 'pointer',
                minHeight: '28px',
                minWidth: '60px',
                transition: 'all 0.2s ease',
                boxShadow: billingPeriod === 'month' ? `0 1px 3px rgba(${hexToRgb(pkg.color)}, 0.3)` : 'none'
              }}
            >
              {language === 'ua' ? 'Місяць' : 'Month'}
            </button>
            <button
              className={`period-btn ${billingPeriod === 'year' ? 'active' : ''}`}
              onClick={() => onBillingPeriodChange('year')}
              style={{
                padding: '4px 10px',
                border: 'none',
                background: billingPeriod === 'year' ? pkg.color : 'transparent',
                color: billingPeriod === 'year' ? '#fff' : '#666',
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: billingPeriod === 'year' ? '600' : '500',
                cursor: 'pointer',
                minHeight: '28px',
                minWidth: '60px',
                transition: 'all 0.2s ease',
                position: 'relative',
                boxShadow: billingPeriod === 'year' ? `0 1px 3px rgba(${hexToRgb(pkg.color)}, 0.3)` : 'none'
              }}
            >
              {language === 'ua' ? 'Рік' : 'Year'}
              {billingPeriod === 'year' && (
                <span style={{
                  position: 'absolute',
                  top: '-6px',
                  right: '-6px',
                  background: '#ff9800',
                  color: '#fff',
                  borderRadius: '8px',
                  padding: '1px 4px',
                  fontSize: '8px',
                  fontWeight: '700',
                  lineHeight: '1',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
                }}>
                  {language === 'ua' ? 'знижка' : 'save'}
                </span>
              )}
            </button>
          </div>

          {/* Currency Toggle */}
          <div style={{
            display: 'flex',
            background: '#f5f5f5',
            border: '1px solid #e0e0e0',
            borderRadius: '6px',
            padding: '2px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            transition: 'all 0.2s ease'
          }}>
            <button
              className={`period-btn ${displayCurrency === 'USD' ? 'active' : ''}`}
              onClick={() => onDisplayCurrencyChange('USD')}
              style={{
                padding: '4px 8px',
                border: 'none',
                background: displayCurrency === 'USD' ? pkg.color : 'transparent',
                color: displayCurrency === 'USD' ? '#fff' : '#666',
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: displayCurrency === 'USD' ? '600' : '500',
                cursor: 'pointer',
                minHeight: '28px',
                minWidth: '45px',
                transition: 'all 0.2s ease',
                boxShadow: displayCurrency === 'USD' ? `0 1px 3px rgba(${hexToRgb(pkg.color)}, 0.3)` : 'none'
              }}
            >
              дол.
            </button>
            <button
              className={`period-btn ${displayCurrency === 'UAH' ? 'active' : ''}`}
              onClick={() => onDisplayCurrencyChange('UAH')}
              style={{
                padding: '4px 8px',
                border: 'none',
                background: displayCurrency === 'UAH' ? pkg.color : 'transparent',
                color: displayCurrency === 'UAH' ? '#fff' : '#666',
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: displayCurrency === 'UAH' ? '600' : '500',
                cursor: 'pointer',
                minHeight: '28px',
                minWidth: '45px',
                transition: 'all 0.2s ease',
                boxShadow: displayCurrency === 'UAH' ? `0 1px 3px rgba(${hexToRgb(pkg.color)}, 0.3)` : 'none'
              }}
            >
              грн.
            </button>
          </div>
        </div>
      </div>

      <div className="master-plan-features">
        {pkg.description && pkg.description.split('\n').filter((line: string) => line.trim() !== '').map((feature: string, index: number) => (
          <div key={index} className="master-feature-item">
            <i className="bi bi-check-circle"></i>
            <span>{feature.trim()}</span>
          </div>
        ))}
        {mode === 'admin' && pkg.max_businesses !== undefined && (
          <>
            <Divider sx={{ my: 1 }} />
            <div className="master-feature-item">
              <i className="bi bi-building"></i>
              <span>{language === 'ua' ? `До ${pkg.max_businesses} бізнесів` : `Up to ${pkg.max_businesses} businesses`}</span>
            </div>
          </>
        )}
        <Divider sx={{ my: 1 }} />
        {/* <div className="master-feature-item">
          <i className={
            //pkg.target_audience === 'clients' ? 'bi bi-people' :
            pkg.target_audience === 'masters' ? 'bi bi-tools' :
            pkg.target_audience === 'owners' ? 'bi bi-star' :
            ''
          }></i>
          <span>
            {pkg.target_audience === 'clients' && (language === 'ua' ? '' : 'For clients')}
            {pkg.target_audience === 'masters' && (language === 'ua' ? 'Для ' : 'For masters')}
            {pkg.target_audience === 'owners' && (language === 'ua' ? 'Для власників' : 'For owners')}
            {!pkg.target_audience && (language === 'ua' ? 'Для клієнтів' : 'For clients')}
          </span>
        </div> */}
      </div>

      <div className="master-plan-select">
        <button
          className="master-select-btn"
          style={{
            backgroundColor: pkg.color,
            borderColor: pkg.color,
            color: '#fff'
          }}
          onClick={() => onSelect(pkg.id)}
        >
          {selectButtonText || (mode === 'admin' ? (language === 'ua' ? 'Редагувати' : 'Edit') : (language === 'ua' ? 'Обрати план' : 'Select Plan'))}
        </button>
      </div>
    </div>
  );
};

export default PackageCard;
